import { useApolloClient } from "@apollo/client";
import firebase from "firebase";
import { Trans, useTranslation } from "next-i18next";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { Text } from "@/features/common/components/tailwind/Text";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { useUser } from "@/features/user/hooks/useUser";
import { trpc } from "@/helpers/trpc";

import { Link } from "../JoinSubscriptionPaymentView";

type JoinSubscriptionByCreditViewProps = {
  onClose: () => void;
  price: number;
  subscriptionPlanId: string;
  isUnlockExpiredPost?: boolean;
  postId?: string;
};

export const JoinSubscriptionByCreditView: React.FC<JoinSubscriptionByCreditViewProps> =
  (props) => {
    const { onClose, price, subscriptionPlanId, isUnlockExpiredPost, postId } =
      props;
    const { user } = useUserContext();
    const { t } = useTranslation(["subscriptionPlan", "common"]);
    const { openSnackbar } = useSnackbar();
    const client = useApolloClient();
    const { mutate: createUserInvoiceMutation, isLoading } =
      trpc.offlinePayment.createUserSubscription.useMutation({
        onSuccess: (res) => {
          onClose();
          openSnackbar({
            message: t("subscriptionPlan:subscribeSuccess"),
            severity: "success",
          });
          client.refetchQueries({
            include: ["subscriptionPlanPage"],
          });
        },
      });
    const handleSubscribeByCredit = () => {
      firebase.analytics().logEvent(AnalyticsEventEnum.SubscribeWithCredit);
      createUserInvoiceMutation({
        subscriptionPlanId,
        postId: isUnlockExpiredPost ? postId : null,
      });
    };

    const { creditBalance } = user;
    return (
      <>
        <OfflinePaymentBreakdown price={price} creditBalance={creditBalance} />
        <div className="w-full text-left text-sm font-light text-gray-900 mt-4">
          <Trans
            i18nKey="common:paymentForm.agree"
            components={{
              privacy: (
                <Link
                  href={RouteName.PrivacyPolicy}
                  label={t("common:terms.privacyPolicy")}
                />
              ),
              termsOfUse: (
                <Link
                  href={RouteName.TermsOfUse}
                  label={t("common:terms.termsOfUse")}
                />
              ),
            }}
          />
        </div>
        <div className="flex justify-end w-full mt-4 space-x-4">
          <OutlinedButton
            variant="secondary"
            buttonProps={{ onClick: onClose, disabled: isLoading }}
          >
            {t("common:cancel")}
          </OutlinedButton>
          <OutlinedButton
            variant="primary"
            buttonProps={{
              onClick: handleSubscribeByCredit,
              disabled: creditBalance < price || isLoading,
            }}
          >
            {t("subscriptionPlan:pay")}
          </OutlinedButton>
        </div>
      </>
    );
  };

const OfflinePaymentBreakdown = ({ price, creditBalance }) => {
  const { t } = useTranslation(["subscriptionPlan", "common"]);
  return (
    <div className="flex flex-col w-full mt-4 space-y-4">
      <div className="flex justify-between">
        <Text size="normal" className="font-light">
          {t("subscriptionPlan:offlinePaymentTable.accountBalance")}
        </Text>
        <Text size="normal">
          {creditBalance}
          {t("subscriptionPlan:offlinePaymentTable.point")}
        </Text>
      </div>
      <div className="flex justify-between">
        <Text size="normal" className="font-light">
          {t("subscriptionPlan:offlinePaymentTable.fee")}
        </Text>
        <Text size="normal">
          -{price}
          {t("subscriptionPlan:offlinePaymentTable.point")}
        </Text>
      </div>
      <div className="flex justify-between">
        <Text size="normal" className="font-light">
          {t("subscriptionPlan:offlinePaymentTable.remainBalance")}
        </Text>
        <Text size="normal">
          {creditBalance - price}
          {t("subscriptionPlan:offlinePaymentTable.point")}
        </Text>
      </div>
    </div>
  );
};
